import { PencilIcon } from 'lucide-react'
import { useState } from 'react'

import { Input } from '@/components/ui'
import { cn, numberToCurrency } from '@/utils'

export const InlineNumber = ({
  value,
  onCommit,
  className,
}: {
  value: number | null
  onCommit: (next: number) => void
  className?: string
}) => {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')

  const start = () => {
    setDraft(value == null ? '' : String(value))
    setEditing(true)
  }

  const commit = () => {
    setEditing(false)
    // An empty field cancels instead of committing 0 (Number('') is 0, not NaN), so clearing a
    // value and blurring never overwrites it with zero.
    const trimmed = draft.trim()
    if (trimmed === '') return
    const parsed = Number(trimmed)
    if (!Number.isNaN(parsed) && parsed !== value) onCommit(parsed)
  }

  if (editing) {
    return (
      <Input
        // biome-ignore lint/a11y/noAutofocus: focus the field the user just clicked to edit
        autoFocus
        type="number"
        step="0.01"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') commit()
          if (event.key === 'Escape') setEditing(false)
        }}
        className={cn('h-7 w-28 text-right tabular-nums', className)}
      />
    )
  }

  return (
    <button
      type="button"
      onClick={start}
      title="Click to edit"
      className={cn(
        'group inline-flex cursor-pointer items-center gap-1 rounded border border-dashed border-muted-foreground/40 bg-muted/40 px-1.5 py-0.5 text-right tabular-nums hover:border-muted-foreground/70 hover:bg-muted',
        className,
      )}
    >
      <PencilIcon className="size-3 shrink-0 text-muted-foreground/60 group-hover:text-foreground" />
      {value == null ? '--' : numberToCurrency(value)}
    </button>
  )
}
